import { TextFieldProps } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import InputMask from 'react-input-mask';

import { currencyMask } from '@utils';

import { InnerLabelTextField, OuterLabelTextField } from '../../common';

const mapMaskTypeToMask = {
  phone: '(999) 999-9999',
  currency: '999999999',
};

type MobileEnterKeyHint = 'next' | 'go';

interface IControlledMaskedTextField {
  label: string;
  /** The name of the module that the field is within. */
  /** The field name within the given module. */
  fieldName: string;
  /** The input mode. */
  inputMode?: TextFieldProps['inputMode'];
  /** Prefixes the input with a dollar sign icon. */
  currencyIcon?: boolean;
  /** The type of mask to apply to the text field. */
  maskType: keyof typeof mapMaskTypeToMask;
  /** Whether the label should render inside or above the text field. */
  labelStyle?: 'inner' | 'outer';
  /** An optional onChange callback that runs after the field is updated in React Hook Form. */
  onChange?: () => void;
  /** Defines the enter label for virtual keyboards. */
  enterKeyHint?: MobileEnterKeyHint;
  /** Callback that is called on the key down event. */
  defaultValue?: string | number;
  /** Informative text to be rendered inside a tooltip. */
  infoTooltipText?: string;
  /** The test ID for the select element */
  helperText?: string;
  placeholder?: string;
  /** Renders a red required asterisk beside the label. */
  required?: boolean;
}

/**
 * An MUI TextField component hooked up to React Hook Form that supports different label styles and different mask types.
 */
export const ControlledMaskedTextField = ({
  fieldName,
  maskType,
  labelStyle = 'inner',
  defaultValue,
  ...props
}: IControlledMaskedTextField) => {
  const mask = mapMaskTypeToMask[maskType];
  const {
    control,
    formState: { touchedFields },
    trigger,
  } = useFormContext();

  const formatAutofill = (phoneNumber: string) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumber;
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => {
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          let value = event.target.value;
          if (maskType === 'phone' && /^\d{10}$/.test(value)) {
            value = formatAutofill(value);
          }
          if (maskType === 'currency') {
            value = value.replace(/[^0-9]/g, '');
          }
          field.onChange(value.toString());
        };

        const errorMessage: string | undefined = fieldState.error
          ?.message as string;

        return (
          <InputMask
            mask={control._formValues[fieldName] ? mask : ''}
            {...field}
            onChange={handleChange}
            onBlur={() => {
              field.onBlur();
              trigger(fieldName);
            }}
            value={field.value?.toString() ?? ''}
            maskPlaceholder={maskType === 'phone' ? '_' : ''}
            beforeMaskedStateChange={
              maskType === 'currency' ? currencyMask : undefined
            }
          >
            {labelStyle === 'outer' ? (
              <OuterLabelTextField
                {...props}
                errorMessage={errorMessage}
                isBlurred={touchedFields[fieldName]}
                fieldName={fieldName as string}
              />
            ) : (
              <InnerLabelTextField
                {...props}
                errorMessage={errorMessage}
                isBlurred={touchedFields[fieldName]}
                fieldName={fieldName as string}
              />
            )}
          </InputMask>
        );
      }}
    />
  );
};
